import { Box, Icon, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'

import HelpCenterLink from 'modules/core/components/HelpCenterLink'
import i18n from 'modules/core/lib/i18n'

import Allowance, { EMPLOYEE_HIRED_DATE } from 'modules/timeoff/store/models/Allowance'
import type { Cycle } from 'modules/timeoff/store/models/AllowanceStat'

import carryOverUnitsDescription from './carryOverUnitsDescription'

const getGeneratedDaysByHoursLabel = (type: string) => {
  switch (type) {
    case 'all_days':
      return i18n.t('time_off.carry_over_explainer.available_hours.generated_days.all_days')
    case 'bimonthly_fifteenth_and_last':
      return i18n.t(
        'time_off.carry_over_explainer.available_hours.generated_days.bimonthly_fifteenth_and_last'
      )
    case 'bimonthly_first_and_fifteenth':
      return i18n.t(
        'time_off.carry_over_explainer.available_hours.generated_days.bimonthly_first_and_fifteenth'
      )
    case 'generated_days':
      return i18n.t('time_off.carry_over_explainer.available_hours.generated_days.generated_days')
    case 'generated_days_monthly':
      return i18n.t(
        'time_off.carry_over_explainer.available_hours.generated_days.generated_days_monthly'
      )
    case 'generated_days_monthly_first_day':
      return i18n.t(
        'time_off.carry_over_explainer.available_hours.generated_days.generated_days_monthly_first_day'
      )
    case 'mensiversary':
      return i18n.t('time_off.carry_over_explainer.available_hours.generated_days.mensiversary')
    case 'monthly_fifteenth':
      return i18n.t(
        'time_off.carry_over_explainer.available_hours.generated_days.monthly_fifteenth'
      )
    default:
      throw new Error(`Unknown translation for type: ${type}`)
  }
}

const getGeneratedDaysByDaysLabel = (type: string) => {
  switch (type) {
    case 'all_days':
      return i18n.t('time_off.carry_over_explainer.available_days.generated_days.all_days')
    case 'bimonthly_fifteenth_and_last':
      return i18n.t(
        'time_off.carry_over_explainer.available_days.generated_days.bimonthly_fifteenth_and_last'
      )
    case 'bimonthly_first_and_fifteenth':
      return i18n.t(
        'time_off.carry_over_explainer.available_days.generated_days.bimonthly_first_and_fifteenth'
      )
    case 'generated_days':
      return i18n.t('time_off.carry_over_explainer.available_days.generated_days.generated_days')
    case 'generated_days_monthly':
      return i18n.t(
        'time_off.carry_over_explainer.available_days.generated_days.generated_days_monthly'
      )
    case 'generated_days_monthly_first_day':
      return i18n.t(
        'time_off.carry_over_explainer.available_days.generated_days.generated_days_monthly_first_day'
      )
    case 'mensiversary':
      return i18n.t('time_off.carry_over_explainer.available_days.generated_days.mensiversary')
    case 'monthly_fifteenth':
      return i18n.t('time_off.carry_over_explainer.available_days.generated_days.monthly_fifteenth')
    default:
      throw new Error(`Unknown translation for type: ${type}`)
  }
}

interface SectionProps {
  title: string
  description: string
}

const Section = ({ title, description }: SectionProps) => (
  <Box alignItems="center">
    <Text.Header3>{title}</Text.Header3>
    <Text.Normal size="200">{description}</Text.Normal>
  </Box>
)

function allowanceUnits(allowance: Allowance, accruedByTimeWorked: number) {
  const title = allowance.isDays
    ? i18n.t('time_off.carry_over_explainer.allowance_days.title')
    : i18n.t('time_off.carry_over_explainer.allowance_hours.title')

  if (allowance.attributes.unlimited_holidays) {
    const description = allowance.isDays
      ? i18n.t('time_off.carry_over_explainer.allowance_days.description.unlimited')
      : i18n.t('time_off.carry_over_explainer.allowance_hours.description.unlimited')

    return {
      title,
      description,
    }
  }

  const workingUnits = allowance.isDays
    ? i18n.t('common.working_days')
    : i18n.t('common.working_hours')

  const naturalUnits = allowance.isDays
    ? i18n.t('common.natural_days')
    : i18n.t('common.natural_hours')

  const count = allowance.isTimeWorked ? Number(accruedByTimeWorked) : allowance.holidayAllowance

  const descriptionArgs = {
    count,
    day_type: allowance.workingDays ? workingUnits : naturalUnits,
  }

  const description = allowance.isDays
    ? i18n.tp('time_off.carry_over_explainer.allowance_days.description.limited', descriptionArgs)
    : i18n.tp('time_off.carry_over_explainer.allowance_hours.description.limited', descriptionArgs)

  return {
    title,
    description,
  }
}

function descriptionByDays(proratedEnabled: boolean, availability: string) {
  if (proratedEnabled) {
    return i18n.t('time_off.carry_over_explainer.available_days.description.proration_enabled', {
      availability,
    })
  }

  return i18n.t('time_off.carry_over_explainer.available_days.description.proration_disabled', {
    availability,
  })
}

function descriptionByHours(proratedEnabled: boolean, availability: string) {
  if (proratedEnabled) {
    return i18n.t('time_off.carry_over_explainer.available_hours.description.proration_enabled', {
      availability,
    })
  }

  return i18n.t('time_off.carry_over_explainer.available_hours.description.proration_disabled', {
    availability,
  })
}

function availableUnits(allowance: Allowance) {
  const allUnits = allowance.isDays
    ? i18n.t('time_off.carry_over_explainer.available_days.all_days')
    : i18n.t('time_off.carry_over_explainer.available_hours.all_days')

  const availableDays = allowance.attributes.available_days

  const generatedUnits = allowance.isDays
    ? getGeneratedDaysByDaysLabel(availableDays)
    : getGeneratedDaysByHoursLabel(availableDays)

  const title = allowance.isDays
    ? i18n.t('time_off.carry_over_explainer.available_days.title')
    : i18n.t('time_off.carry_over_explainer.available_hours.title')

  const availability =
    allowance.attributes.available_days === Allowance.AVAILABLE_OPTIONS_DAYS.all_days
      ? allUnits
      : generatedUnits

  const description = allowance.isDays
    ? descriptionByDays(allowance.hasProrationEnabled, availability)
    : descriptionByHours(allowance.hasProrationEnabled, availability)

  return {
    title,
    description,
  }
}

function carryOverUnits(allowance: Allowance, cycle: Cycle) {
  const title = allowance.isDays
    ? i18n.t('time_off.carry_over_explainer.carry_over_days.title')
    : i18n.t('time_off.carry_over_explainer.carry_over_hours.title')

  return {
    title,
    description: carryOverUnitsDescription(allowance, cycle),
  }
}

function timeoffCycleToDate(
  allowance: Allowance,
  cycle: Cycle
): {
  start_on: string
  finish_on: string
} {
  const { begin, end } = cycle

  const format = allowance.attributes.timeoff_cycle === EMPLOYEE_HIRED_DATE ? 'DD MMM YYYY' : 'MMM'

  return {
    start_on: begin.format(format),
    finish_on: end.format(format),
  }
}

function allowanceCycle(allowance: Allowance, cycle: Cycle) {
  const title = i18n.t('time_off.carry_over_explainer.allowance_cycle.title')

  return {
    title,
    description: i18n.t(
      'time_off.carry_over_explainer.allowance_cycle.description',
      timeoffCycleToDate(allowance, cycle)
    ),
  }
}

interface Props {
  allowance: Allowance
  cycle: Cycle
  accruedByTimeWorked: number
}

export default function CarryOverExplainer({ allowance, cycle, accruedByTimeWorked }: Props) {
  const [open, toggle] = useToggle()

  if (!allowance) return null

  const sections = [
    allowanceUnits(allowance, accruedByTimeWorked),
    availableUnits(allowance),
    carryOverUnits(allowance, cycle),
    allowanceCycle(allowance, cycle),
  ]

  return (
    <Box alignItems="center">
      <Tooltip.ItemList
        attachment="top center"
        targetAttachment="bottom center"
        open={open}
        toggle={toggle}
      >
        <Box
          gap="s8"
          alignItems="center"
          cursor="pointer"
          flexDirection="row"
          justifyContent="spaceBetween"
        >
          <Icon.Medium icon="info-circle-line" color="grey800" />
        </Box>
        {sections.map(({ title, description }: SectionProps, i: number) => (
          <Section key={`${title}-${i}`} title={title} description={description} />
        ))}
        <HelpCenterLink name="timeoff_counters">
          {i18n.t('time_off.carry_over_explainer.help_link')}
        </HelpCenterLink>
      </Tooltip.ItemList>
    </Box>
  )
}
